import { BOX_CAPACITY_GRAM, BOX_SIZE, BOX_SMALL_MAX_GRAM, BOX_TARE_GRAM } from "../constants/PickingConstant";
import { VARIATION_WEIGHT } from "../constants/VariationConstant";

/**
 * How many boxes an order becomes, and how big each one is.
 *
 * A 1:1 port of the api's SooCoolColliService (packOrder, expandProduct, the barcode rules and
 * parseNumberOrNull) - deliberately a copy and not a new algorithm. The api split is what SooCool
 * gets told, labels and bills: if the packing slip advised a different number of boxes than the
 * number of labels that roll out of the printer, the picker would have to choose which one to
 * believe. So this answers "which boxes" exactly the way the api does, and only adds a name per
 * box: KLEIN up to 7 kg, GROOT above.
 *
 * Per piece, after expanding packages (also packages inside packages, three levels deep):
 *
 *   real        = variation weight | WEIGHT item value ?? product.pickWeightGram ?? 0
 *   volume      = product.volumeWeightGram ?? real
 *   chargeable  = max(real, volume)
 *
 * The pieces are spread over boxes first-fit-decreasing on the chargeable weight, into a net
 * capacity of BOX_CAPACITY_GRAM - BOX_TARE_GRAM. The real weight plus tare is what a box is
 * declared to weigh; a box with no weight data at all declares nothing rather than a guess.
 *
 * Unknown products count as 0 gram here, as they do in the api: this is a prediction, not the
 * pick list. The pick list (functions/pickLines) refuses an order with an unknown product.
 *
 * `catalogue` is anything with `get(productId)` - a Map in tests, `catalogueForShop()` in the CMS.
 * Pure: no Firestore, no React.
 */

export const MAX_UNITS = 5000;
export const MAX_PACKAGE_DEPTH = 3;

const CONTENTS_MAX_LENGTH = 255;
const BARCODE_MIN_LENGTH = 6;
const BARCODE_MAX_PREFIX_LENGTH = 90;
const BARCODE_DISALLOWED = /[^A-Za-z0-9\-.]/g;
const LEADING_NUMBER = /^-?[0-9]+(\.[0-9]+)?/;

/** Net room in one box: the gross limit minus the empty box with its insulation. */
export const netCapacityGram = () => BOX_CAPACITY_GRAM - BOX_TARE_GRAM;

/**
 * A gram value or a piece count as the hand-maintained catalogue stores it: 500, "500",
 * "500 gram". Rounded to a whole number; null when there is no usable value. Unlike
 * productSync.normaliseGrams this accepts "500 gram" - on purpose, because the api does, and the
 * two sides must read polluted data the same way.
 */
export const parseNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? Math.round(value) : null;
  const text = String(value).trim();
  if (text === "") return null;
  const match = LEADING_NUMBER.exec(text);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
};

const lookup = (catalogue, productId) => (!productId || !catalogue ? undefined : catalogue.get(productId));

const isPackage = (product) => Array.isArray(product?.productItems) && product.productItems.length > 0;

/**
 * Expand one product - a plain article or a package - into pieces. Mirrors expandProduct in the
 * api line for line, including what happens to a package that is not followed (too deep, or
 * containing itself): it keeps its own weight instead of disappearing.
 */
const expandProduct = (units, catalogue, productId, product, name, knownWeight, quantity, ancestry, depth, missing) => {
  if (quantity <= 0) return;

  const items = product ? product.productItems : null;
  const selfContaining = !!productId && ancestry.has(productId);

  if (isPackage(product) && depth < MAX_PACKAGE_DEPTH && !selfContaining) {
    const nestedAncestry = new Set(ancestry);
    if (productId) nestedAncestry.add(productId);

    items.forEach((item) => {
      if (!item) return;
      const sub = lookup(catalogue, item.productId);
      const weighed = item.type === VARIATION_WEIGHT;

      // A WEIGHT item carries its count in `quantity` and its weight in `value`; a PIECES item
      // carries its count in `value`. A missing WEIGHT count means one piece.
      let perPackage = parseNumberOrNull(weighed ? item.quantity : item.value);
      if (perPackage === null && weighed) perPackage = 1;
      const itemQuantity = (perPackage === null ? 0 : perPackage) * quantity;
      if (itemQuantity <= 0) return;

      const itemWeight = weighed ? parseNumberOrNull(item.value) : null;
      const itemName = sub && sub.name ? sub.name : item.productId;

      expandProduct(units, catalogue, item.productId, sub, itemName, itemWeight, itemQuantity, nestedAncestry, depth + 1, missing);
    });
    return;
  }

  let weight = knownWeight;
  if (weight === null && product) weight = parseNumberOrNull(product.pickWeightGram);
  if (weight === null && missing) {
    const key = !productId ? `naam:${name}` : productId;
    if (!missing.has(key)) missing.set(key, { productId: productId || null, name });
  }
  const realGram = weight === null ? 0 : weight;

  const volume = product ? parseNumberOrNull(product.volumeWeightGram) : null;
  const volumeGram = volume === null ? realGram : volume;

  if (units.length + quantity > MAX_UNITS) {
    throw new Error(
      `Te veel losse artikelen om in dozen te verdelen (meer dan ${MAX_UNITS}). Controleer de aantallen en de pakketinhoud van deze bestelling.`,
    );
  }
  for (let index = 0; index < quantity; index += 1) {
    units.push({ name, realGram: Math.max(realGram, 0), chargeableGram: Math.max(Math.max(realGram, volumeGram), 0) });
  }
};

const expand = (order, catalogue, missing) => {
  const units = [];
  (order?.products || []).forEach((line) => {
    if (!line || line.giftCard) return;
    const quantity = line.quantity === null || line.quantity === undefined ? 0 : Number(line.quantity);
    if (!(quantity > 0)) return;

    const name = line.name ? line.name : line.productId;
    const variationWeight = parseNumberOrNull(line.variation ? line.variation.weight : null);
    expandProduct(units, catalogue, line.productId, lookup(catalogue, line.productId), name, variationWeight, quantity, new Set(), 0, missing);
  });
  return units;
};

const truncate = (value, maxLength) => (value.length <= maxLength ? value : `${value.slice(0, maxLength - 3)}...`);

/** "3x Kipfilet, 1x Rundergehakt" - what is in a box, in the order it was met. */
const contentsFor = (units, order) => {
  if (units.length === 0) {
    const reference = order && order.orderId ? order.orderId : "bestelling";
    return truncate(`Bestelling ${reference}`, CONTENTS_MAX_LENGTH);
  }
  const counts = new Map();
  units.forEach((unit) => {
    const name = unit.name ? unit.name : "Artikel";
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  return truncate(
    [...counts.entries()].map(([name, count]) => `${count}x ${name}`).join(", "),
    CONTENTS_MAX_LENGTH,
  );
};

const barcodePrefix = (order) => {
  let cleaned = String((order && order.orderId) || "").replace(BARCODE_DISALLOWED, "");
  if (cleaned === "") cleaned = "ORDER";
  return cleaned.length > BARCODE_MAX_PREFIX_LENGTH ? cleaned.slice(0, BARCODE_MAX_PREFIX_LENGTH) : cleaned;
};

/** The colli barcode SooCool gets for box `number` of this order: `{orderId}-{number}`. */
export const colliBarcode = (orderId, number) => {
  let barcode = `${barcodePrefix({ orderId })}-${number}`;
  while (barcode.length < BARCODE_MIN_LENGTH) barcode = `0${barcode}`;
  return barcode;
};

/**
 * The boxes this order becomes. Always at least one: an order with nothing that weighs anything
 * still needs a label.
 *
 * @returns {Array<{n, chargeableGram, declaredGram: number|null, contents, barcode}>}
 */
export const packOrder = (order, catalogue, { capacityGram = netCapacityGram(), tareGram = BOX_TARE_GRAM } = {}) => {
  if (!(capacityGram >= 1)) throw new Error(`capacityGram moet minimaal 1 zijn, was ${capacityGram}`);
  const units = expand(order, catalogue, null);
  const orderId = order ? order.orderId : null;

  if (units.length === 0) {
    return [{ n: 1, chargeableGram: 0, declaredGram: null, contents: contentsFor([], order), barcode: colliBarcode(orderId, 1) }];
  }

  // Heaviest first, into the first box it still fits in. A piece heavier than a whole box opens
  // its own box - one piece is one piece, that is the honest answer.
  units.sort((left, right) => right.chargeableGram - left.chargeableGram);
  const boxes = [];
  units.forEach((unit) => {
    let target = boxes.find((box) => box.chargeableGram + unit.chargeableGram <= capacityGram);
    if (!target) {
      target = { units: [], chargeableGram: 0, realGram: 0 };
      boxes.push(target);
    }
    target.units.push(unit);
    target.chargeableGram += unit.chargeableGram;
    target.realGram += unit.realGram;
  });

  return boxes.map((box, index) => ({
    n: index + 1,
    chargeableGram: box.chargeableGram,
    declaredGram: box.realGram > 0 ? box.realGram + Math.max(tareGram, 0) : null,
    contents: contentsFor(box.units, order),
    barcode: colliBarcode(orderId, index + 1),
  }));
};

/**
 * Products that ended up weighing nothing - no pickWeightGram and no weight on the variation or
 * package item. Not an error, but it makes the box advice quietly unreliable, so it is shown.
 * Found through the same expansion as the packing, so the two can never disagree.
 */
export const findProductsWithoutWeightData = (order, catalogue) => {
  const missing = new Map();
  expand(order, catalogue, missing);
  return [...missing.values()];
};

/** KLEIN up to 7 kg chargeable, GROOT above. */
export const tierFor = (chargeableGram) => (chargeableGram <= BOX_SMALL_MAX_GRAM ? BOX_SIZE.SMALL : BOX_SIZE.LARGE);

/** The boxes with their size, plus what the prediction could not weigh. */
export const predictBoxes = (order, catalogue, options) => ({
  boxes: packOrder(order, catalogue, options).map((box) => ({ ...box, size: tierFor(box.chargeableGram) })),
  productsWithoutWeight: findProductsWithoutWeightData(order, catalogue),
});

const BOX_NAMES = { [BOX_SIZE.LARGE]: "GROTE DOOS", [BOX_SIZE.SMALL]: "KLEINE DOOS" };

/** "KLEINE DOOS", "GROTE DOOS", "2 × GROTE DOOS", "2 × GROTE DOOS + KLEINE DOOS". */
export const formatBoxAdvice = (boxes) => {
  const counts = [BOX_SIZE.LARGE, BOX_SIZE.SMALL]
    .map((size) => ({ size, count: (boxes || []).filter((box) => box.size === size).length }))
    .filter(({ count }) => count > 0);
  if (counts.length === 0) return BOX_NAMES[BOX_SIZE.SMALL];
  return counts.map(({ size, count }) => (count === 1 ? BOX_NAMES[size] : `${count} × ${BOX_NAMES[size]}`)).join(" + ");
};
